import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import type { ObjectComponent } from '@sizzlenz/shared';
import {
  OBJECT_COMPONENT_CATEGORY_OPTIONS,
  OBJECT_COMPONENT_STATUS_OPTIONS,
  OBJECT_COMPONENT_COMPENSATION_OPTIONS,
} from '@sizzlenz/shared';

function getLabelForValue<T extends { value: string; label: string }>(
  options: T[],
  value: string
): string {
  return options.find((o) => o.value === value)?.label || value;
}

interface ObjectComponentItemProps {
  component: ObjectComponent;
  onEdit?: () => void;
  onRemove?: () => void;
  /** When true, show the full details text without truncation. */
  expanded?: boolean;
}

export function ObjectComponentItem({
  component,
  onEdit,
  onRemove,
  expanded,
}: ObjectComponentItemProps) {
  const editable = !!onEdit || !!onRemove;

  return (
    <div className="flex items-start gap-3 rounded-lg border p-3 bg-white">
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-medium text-sm">{component.title}</span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            {getLabelForValue(OBJECT_COMPONENT_CATEGORY_OPTIONS, component.category)}
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            {getLabelForValue(OBJECT_COMPONENT_STATUS_OPTIONS, component.status)}
          </span>
          <span className="text-xs px-1.5 py-0.5 rounded bg-muted text-muted-foreground">
            {getLabelForValue(OBJECT_COMPONENT_COMPENSATION_OPTIONS, component.compensation)}
          </span>
          {component.needsProvider && (
            <span className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
              Needs Provider
            </span>
          )}
        </div>
        {component.details && (
          <p
            className={`text-sm text-muted-foreground mt-1 whitespace-pre-line${expanded ? '' : ' line-clamp-2'}`}
          >
            {component.details}
          </p>
        )}
      </div>
      {editable && (
        <div className="flex items-center gap-1 shrink-0">
          {onEdit && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onEdit}
              className="h-8 w-8 cursor-pointer"
            >
              <Pencil className="h-3.5 w-3.5" />
            </Button>
          )}
          {onRemove && (
            <Button
              type="button"
              variant="ghost"
              size="icon"
              onClick={onRemove}
              className="h-8 w-8 cursor-pointer text-destructive hover:text-destructive"
            >
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
